/**
 * Utilidades de UI para devoluciones parciales/totales.
 * El backend vuelve a validar saldos; aquí solo se limita la captura.
 */
import { paymentTabLabel } from './legacyPosLabels';
import { copyProductPrices } from './productPriceList';
import { calculateTicketTaxLines } from './posTicketLogic';

export const REFUND_PAYMENT_METHODS = [
  { paymentType: 'cashrefund', label: paymentTabLabel('cashrefund'), permission: 'refund.cash' },
  { paymentType: 'chequerefund', label: paymentTabLabel('chequerefund'), permission: 'refund.cheque' },
  { paymentType: 'paperout', label: paymentTabLabel('paper'), permission: 'refund.paper' },
  { paymentType: 'magcardrefund', label: paymentTabLabel('magcardrefund'), permission: 'refund.magcard' },
];

export const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;
export const roundQty = (value) => Math.round(Number(value || 0) * 1000) / 1000;

/**
 * Construye el estado editable de líneas a devolver a partir del detalle.
 * @param {Array} lines
 * @returns {Array}
 */
export const buildRefundLineDrafts = (lines = []) => (
  lines.map((line) => {
    const available = roundQty(Number(line.available != null ? line.available : line.units));
    return {
      originLine: Number(line.line),
      productId: line.productId,
      productName: line.productName,
      productCode: line.productCode,
      price: Number(line.price || 0),
      taxId: line.taxId,
      taxCategoryId: line.taxCategoryId,
      categoryId: line.categoryId,
      categoryName: line.categoryName,
      isDiscountLine: Boolean(line.isDiscountLine),
      sold: roundQty(Number(line.sold != null ? line.sold : Math.abs(line.units || 0))),
      refunded: roundQty(Number(line.refunded || 0)),
      available,
      selected: available > 0,
      unitsToRefund: available > 0 ? available : 0,
    };
  })
);

export const computeRefundSubtotal = (draftLines = []) => (
  roundMoney(
    draftLines
      .filter((line) => line.selected && Number(line.unitsToRefund) > 0)
      .reduce((sum, line) => sum + (-Math.abs(Number(line.unitsToRefund)) * Number(line.price || 0)), 0),
  )
);

/**
 * Convierte la selección de devolución a líneas fiscales negativas.
 * El signo debe coincidir con las líneas que persistirá el backend.
 */
export const buildRefundTaxInputLines = (draftLines = []) => (
  draftLines
    .filter((line) => line.selected && Number(line.unitsToRefund) > 0)
    .map((line) => ({
      units: -Math.abs(Number(line.unitsToRefund)),
      price: Number(line.price || 0),
      taxId: line.taxId,
    }))
);

/** Calcula los impuestos negativos de la devolución con las reglas Openbravo. */
export const computeRefundTaxes = (draftLines = [], taxes = []) => {
  const taxLines = calculateTicketTaxLines(buildRefundTaxInputLines(draftLines), taxes);
  return roundMoney(taxLines.reduce((sum, line) => sum + Number(line.amount || 0), 0));
};

/** Devuelve el desglose fiscal que debe cuadrar con PAYMENTS. */
export const computeRefundFiscalTotals = (draftLines = [], taxes = []) => {
  const subtotal = computeRefundSubtotal(draftLines);
  const tax = computeRefundTaxes(draftLines, taxes);
  return {
    subtotal,
    tax,
    total: roundMoney(subtotal + tax),
  };
};

export const buildRefundRequestLines = (draftLines = []) => (
  draftLines
    .filter((line) => line.selected && Number(line.unitsToRefund) > 0 && Number(line.available) > 0)
    .map((line) => ({
      originLine: Number(line.originLine),
      units: roundQty(Math.min(Number(line.unitsToRefund), Number(line.available))),
    }))
);

export const clampRefundUnits = (value, available) => {
  const qty = Number(value);
  const max = Number(available);
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  if (!Number.isFinite(max) || max <= 0) return 0;
  return roundQty(Math.min(qty, max));
};

export const ticketTypeLabel = (ticketType) => {
  const type = Number(ticketType);
  if (type === 1) return 'Devolución';
  if (type === 2) return 'Pago';
  return 'Venta';
};

export const ticketTypeColor = (ticketType) => {
  const type = Number(ticketType);
  if (type === 1) return 'orange';
  if (type === 2) return 'blue-grey';
  return 'primary';
};

/**
 * Convierte líneas de capabilities/detalle a líneas editables estilo POS.
 * @param {Array} lines
 * @returns {Array}
 */
export const buildEditLinesFromTicket = (lines = []) => (
  lines
    .filter((line) => Number(line.units || 0) !== 0)
    .map((line, index) => ({
      id: `edit-${Number(line.line)}-${index}`,
      originLine: Number(line.line),
      productId: line.productId,
      code: line.productCode || '',
      name: line.productName || 'Producto',
      quantity: Number(line.units || 0),
      unitPrice: Number(line.price || 0),
      taxId: line.taxId,
      taxCat: line.taxCategoryId,
      categoryId: line.categoryId,
      categoryName: line.categoryName,
      isDiscountLine: Boolean(line.isDiscountLine),
      ...copyProductPrices(line),
    }))
);

/**
 * Payload de líneas para PUT /pos/tickets/:id (reemplazo).
 * @param {Array} editLines
 * @returns {Array}
 */
export const buildReplaceRequestLines = (editLines = []) => (
  editLines
    .filter((line) => Number(line.quantity) !== 0)
    .map((line) => ({
      productId: line.productId,
      productName: line.name,
      productCode: line.code,
      units: Number(line.quantity),
      price: Number(line.unitPrice),
      taxId: line.taxId,
      taxCategoryId: line.taxCat || line.taxCategoryId,
      categoryId: line.categoryId,
      categoryName: line.categoryName,
      isDiscountLine: Boolean(line.isDiscountLine),
    }))
);

/**
 * Total (sin impuestos de pantalla) de líneas editables.
 * El backend recalcula TAXLINES; aquí se muestra subtotal de líneas.
 */
export const computeEditSubtotal = (editLines = []) => (
  roundMoney(
    editLines.reduce(
      (sum, line) => sum + (Number(line.quantity || 0) * Number(line.unitPrice || 0)),
      0,
    ),
  )
);

/**
 * Agrega una unidad devolvible al borrador de devolución (modo POS).
 * Solo admite líneas del ticket original (originLine).
 */
export const addRefundUnitFromOrigin = (draftLines, originLine, units = 1) => {
  const target = draftLines.find((line) => Number(line.originLine) === Number(originLine));
  if (!target || Number(target.available) <= 0) return draftLines;
  const next = clampRefundUnits(
    Number(target.unitsToRefund || 0) + Math.abs(Number(units) || 0),
    target.available,
  );
  return draftLines.map((line) => (
    Number(line.originLine) === Number(originLine)
      ? { ...line, selected: next > 0, unitsToRefund: next }
      : line
  ));
};

/** Agrega toda la cantidad disponible de una línea origen. */
export const addRefundFullLineFromOrigin = (draftLines, originLine) => {
  const target = draftLines.find((line) => Number(line.originLine) === Number(originLine));
  if (!target) return draftLines;
  return addRefundUnitFromOrigin(draftLines, originLine, target.available);
};

/** Marca todas las líneas con su disponible completo. */
export const selectAllRefundAvailable = (draftLines = []) => (
  draftLines.map((line) => ({
    ...line,
    selected: Number(line.available) > 0,
    unitsToRefund: Number(line.available) > 0 ? Number(line.available) : 0,
  }))
);
